import React from 'react';
import { User, Lock } from 'lucide-react';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import AuthField from './AuthField';
import { USERNAME_HINT, normalizeUsername } from './credentialSchema';

/**
 * The account-creation block shared by every surface that mints a login:
 * /register (self-serve, which also creates an organization) and
 * /accept-invitation (which joins an existing one via a token).
 *
 * Only the credential half is shared. What surrounds it is genuinely
 * different — self-serve signup collects an organization and an editable
 * email; an invitation's email is fixed by the token and must NOT be editable,
 * or the link could be redeemed for another address — so those stay with their
 * pages. Consolidating further would mean bending one flow to the other.
 *
 * The rules come from ./credentialSchema, which mirrors the route validators.
 */
export interface AccountCredentialFieldsProps {
  /* react-hook-form is generic over the form shape; these surfaces have
     different shapes around the shared fields, so the register/errors pair is
     accepted loosely rather than forcing both pages onto one type. */
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  /** Icons suit the narrow invitation card; the wide signup form reads better without. */
  withIcons?: boolean;
}

const message = (errors: FieldErrors<any>, key: string): string | undefined => {
  const entry = errors[key] as { message?: unknown } | undefined;
  return typeof entry?.message === 'string' ? entry.message : undefined;
};

const AccountCredentialFields: React.FC<AccountCredentialFieldsProps> = ({
  register,
  errors,
  withIcons = false,
}) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <AuthField
        id="firstName"
        label="First Name"
        required
        autoComplete="given-name"
        placeholder="Your first name"
        icon={withIcons ? User : undefined}
        registration={register('firstName')}
        error={message(errors, 'firstName')}
      />
      <AuthField
        id="lastName"
        label="Last Name"
        required
        autoComplete="family-name"
        placeholder="Your last name"
        icon={withIcons ? User : undefined}
        registration={register('lastName')}
        error={message(errors, 'lastName')}
      />
    </div>

    <AuthField
      id="username"
      label="Username"
      required
      hint={USERNAME_HINT}
      autoComplete="username"
      placeholder="Choose a username"
      icon={withIcons ? User : undefined}
      registration={register('username', { setValueAs: normalizeUsername })}
      error={message(errors, 'username')}
    />

    <AuthField
      id="password"
      label="Password"
      type="password"
      required
      autoComplete="new-password"
      placeholder="Create a secure password"
      icon={withIcons ? Lock : undefined}
      registration={register('password')}
      error={message(errors, 'password')}
    />

    <AuthField
      id="confirmPassword"
      label="Confirm Password"
      type="password"
      required
      autoComplete="new-password"
      placeholder="Confirm your password"
      icon={withIcons ? Lock : undefined}
      registration={register('confirmPassword')}
      error={message(errors, 'confirmPassword')}
    />
  </>
);

export default AccountCredentialFields;
